import React, { useState, useEffect, useCallback } from 'react';
import { getNacionalidadesPaginadas, deleteNacionalidad, darDeBajaNacionalidad } from '../services/nacionalidadService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function NacionalidadesList() {
    const [nacionalidades, setNacionalidades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const navigate = useNavigate();
    const { token } = useAuth();

    // Debounce manual para evitar muchas consultas al tipear rápido
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Carga de datos que depende de los estados de paginación
    const cargarNacionalidades = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getNacionalidadesPaginadas(token, page, pageSize, debouncedSearch);
            setNacionalidades(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setError(null);
        } catch (error) {
            console.error('Error al cargar nacionalidades:', error);
            setError('No se pudieron cargar las nacionalidades.');
            toast.error('Error al cargar las nacionalidades');
        } finally {
            setLoading(false);
        }
    }, [token, page, pageSize, debouncedSearch]);

    useEffect(() => {
        cargarNacionalidades();
    }, [cargarNacionalidades]);

    // Si cambia la busqueda, volver a la pagina 0
    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, pageSize]);

    const handleDarDeBaja = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas desactivar esta nacionalidad?')) {
            try {
                await darDeBajaNacionalidad(token, id);
                toast.success('Nacionalidad desactivada exitosamente');
                cargarNacionalidades();
            } catch (error) {
                console.error('Error al desactivar:', error);
                toast.error('No se pudo desactivar la nacionalidad');
            }
        }
    };

    // Calcular el rango mostrado Ej: "Mostrando 11 a 20"
    const startItem = totalElements === 0 ? 0 : page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion#personas')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Volver a Parametrización"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h1 className="text-lg font-semibold tracking-tight">Mantenimiento: Catálogo de Nacionalidades</h1>
                </div>

                <Link
                    to="/parametrizacion/nacionalidades/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[0.85rem] font-medium px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVA NACIONALIDAD
                </Link>
            </div>

            {/* Controls */}
            <div className="p-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                <div className="flex items-center gap-2">
                    <label className="text-xs font-bold text-gray-600 uppercase">Mostrar:</label>
                    <select
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                        className="border border-gray-300 p-1 bg-white focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm disabled:opacity-50"
                        disabled={loading}
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                    </select>
                    <span className="text-sm text-gray-500 italic">registros por página</span>
                </div>

                <div className="relative">
                    <input
                        type="text"
                        placeholder="Buscar nacionalidad..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border border-gray-300 pl-8 pr-3 py-1.5 w-64 focus:ring-1 focus:ring-blue-500 outline-none text-[0.875rem] rounded-sm"
                    />
                    <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
                </div>
            </div>

            {/* Content Table Area */}
            <div className="overflow-x-auto relative">
                {error && (
                    <div className="m-3 p-3 bg-red-50 border border-red-200 text-red-700 flex items-center rounded-sm text-sm">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        {error}
                    </div>
                )}

                <div className="min-w-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-gray-700 text-xs uppercase tracking-wider">
                                <th className="px-4 py-3 border-r border-gray-200 w-16 text-center">ID</th>
                                <th className="px-4 py-3 border-r border-gray-200">País (Nacionalidad)</th>
                                <th className="px-4 py-3 border-r border-gray-200 w-32 text-center">Estado</th>
                                <th className="px-4 py-3 text-center w-40">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm text-gray-600">
                            {loading && nacionalidades.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-4 py-8 text-center">
                                        <RefreshCw className="w-6 h-6 text-blue-500 animate-spin mx-auto" />
                                    </td>
                                </tr>
                            ) : nacionalidades.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500 italic">
                                        {searchTerm
                                            ? `No se encontraron resultados para "${searchTerm}"`
                                            : 'No hay nacionalidades registradas.'}
                                    </td>
                                </tr>
                            ) : (
                                nacionalidades.map((nacionalidad) => (
                                    <tr key={nacionalidad.id} className="border-b border-gray-200 hover:bg-[#f1f5f9] transition-colors">
                                        <td className="px-4 py-2 border-r border-gray-200 text-center font-mono text-xs text-gray-500">
                                            {nacionalidad.id}
                                        </td>
                                        <td className="px-4 py-2 border-r border-gray-200 font-medium text-gray-800">
                                            {nacionalidad.pais.toUpperCase()}
                                        </td>
                                        <td className="px-4 py-2 border-r border-gray-200 text-center">
                                            {nacionalidad.activo ? (
                                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-bold border border-green-200 rounded-sm">ACTIVO</span>
                                            ) : (
                                                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs font-bold border border-gray-200 rounded-sm">INACTIVO</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-center flex justify-center items-center gap-2">
                                            <Link
                                                to={`/parametrizacion/nacionalidades/${nacionalidad.id}/editar`}
                                                className="text-blue-600 hover:text-blue-800 font-semibold text-xs transition-colors"
                                                title="Editar"
                                            >
                                                EDITAR
                                            </Link>
                                            {nacionalidad.activo && (
                                                <>
                                                    <span className="text-gray-300">|</span>
                                                    <button
                                                        onClick={() => handleDarDeBaja(nacionalidad.id)}
                                                        className="text-red-600 hover:text-red-800 font-semibold text-xs transition-colors"
                                                        title="Dar de Baja"
                                                    >
                                                        ELIMINAR
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Paginator Footer */}
            <div className="p-3 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-2 border-t border-gray-200 text-sm">
                <div className="text-gray-500">
                    Mostrando <span className="font-semibold text-gray-700">{startItem} a {endItem}</span> de <span className="font-semibold text-gray-700">{totalElements}</span> registros
                    {loading && <RefreshCw className="inline-block w-3.5 h-3.5 ml-2 animate-spin text-gray-400" />}
                </div>

                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setPage(0)}
                        disabled={page === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm"
                        title="Primero"
                    >
                        <ChevronsLeft className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm"
                        title="Anterior"
                    >
                        <ChevronLeft className="w-4 h-4" />
                    </button>

                    <button className="px-3 py-1 border border-blue-600 bg-blue-600 text-white font-bold rounded-sm">
                        {page + 1}
                    </button>
                    <span className="text-gray-400 text-xs px-1">de {totalPages || 1}</span>

                    <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages - 1 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm"
                        title="Siguiente"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setPage(totalPages - 1)}
                        disabled={page >= totalPages - 1 || totalPages === 0 || loading}
                        className="px-2 py-1 border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-50 text-gray-600 rounded-sm"
                        title="Último"
                    >
                        <ChevronsRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}